import { SvgIcon, Box, TextField, alpha, ButtonGroup, Button, Typography, InputProps } from '@mui/material';
import React from 'react';
import { spaces, customSizes, palette, borderRadius } from '../../../assets/styles/themes/tokens';
import getMuiIcon from '../../../utils/getMuiIcon';
import { colors } from '../../../assets/styles/themes/variables';
import { useTranslation } from 'react-i18next';

interface SearchInputProps {
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  endAdornment?: string;
  placeholder?: string;
  isDisabled?: boolean;
  onMouseOver?: () => void;
  inputProps?: InputProps;
  value?: string;
}

export const searchInputId = 'search-input';

export const focusOnCustomSearch = () => {
  const searchInput = document.getElementById(searchInputId);
  if (searchInput) {
    searchInput.focus();
  }
};

const SearchInput : React.FC<SearchInputProps> = (props) => {
  const { t } = useTranslation();
  const arrowLeft = <SvgIcon component={getMuiIcon('arrowLeft')} />;
  // rotate the arrow 180 degrees with css
  const arrowRight = <SvgIcon
    component={getMuiIcon('arrowLeft')}
    sx={{ transform: 'rotate(180deg)' }}
  />;
  
  return <Box
    onMouseOver={props.onMouseOver}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: spaces.xxs,
    }}
    data-testid='search-input'
  >
    <TextField 
      onChange={props.onChange}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          props.onNext && props.onNext();
        }
      }}
      data-testid='search-document-text-field'
      variant='outlined'
      size='small'
      disabled={props.isDisabled}
      placeholder={props.placeholder || t('Search document')}
      value={props.value}
      sx={{
        height: customSizes.searchInput.height,
        ...props.inputProps?.sx,
      }}
      InputProps={{
        id: searchInputId,
        sx: {
          '& input': {
            padding: `${spaces.xs} 0`,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${palette.components.input.outlined.enabledBorder}`,
            borderRadius: borderRadius.xs,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${palette.components.input.outlined.enabledBorder}`,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${palette.components.input.outlined.enabledBorder}`,
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${palette.components.input.outlined.enabledBorder}`,
          },
          height: '100%',
          padding: `0 ${spaces.xs}`,
          border: 'none',
        },
        startAdornment: <SvgIcon
          component={getMuiIcon('search')}
          sx={{
            width: 20,
            height: 20,
            marginRight: spaces.xs,
          }}
          viewBox='0 0 17 17'
        />,
        endAdornment: <Typography variant='caption' sx={{ color: palette.text.disabled }}>
          {props.endAdornment}
        </Typography>,
      }}
    />
    <ButtonGroup color='secondary' variant='outlined' disabled={props.isDisabled}
      sx={{
        borderRadius: borderRadius.xs,
        '& .MuiButton-root': {
          borderColor: palette.components.input.outlined.enabledBorder,
          color: alpha(colors.neutral800, 0.56),
        },
        '& .MuiButton-root:hover': {
          borderColor: palette.components.input.outlined.enabledBorder,
          color: alpha(colors.neutral800, 0.56),
        },
      }}
    >
      <Button size='small' onClick={props.onPrevious} data-testid='prev-search-button'>
        {arrowLeft}
      </Button>
      <Button size='small' onClick={props.onNext} data-testid='next-search-button'>
        {arrowRight}
      </Button>
    </ButtonGroup>
  </Box>;
};

export default SearchInput;
